import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { UnifiedMaterial } from '@/types/unifiedLibrary';
import { dataSyncEvents } from '@/utils/dataSyncEvents';
import { useAuth } from '@/components/Auth/AuthProvider';
import { CurrencySelect } from '@/components/CurrencySelect';
import { Package, Settings, Database, Copy, Star, Leaf, Zap, Recycle, AlertTriangle, Clock, Box, TestTube, Shield, Thermometer, Droplets } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface EnhancedMaterialModalProps {
  material: UnifiedMaterial | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const EnhancedMaterialModal = ({ material, isOpen, onClose, onSuccess }: EnhancedMaterialModalProps) => {
  const [formData, setFormData] = useState({
    customDensity: '',
    customCarbonFootprint: '',
    customCostPerUnit: '',
    customDescription: '',
    customUnit: '',
    customCurrency: 'USD',
    customMetadata: '',
    isCustom: false,
    reportingPeriod: new Date().toISOString().split('T')[0],
    dataSource: 'Library Import',
    scope: '',
    validationStatus: 'pending',
    // New fields for enhanced material properties
    customEnvironmentalImpactScore: '',
    customRecyclability: '',
    customBiodegradability: '',
    customAvailability: '',
    customLeadTime: '',
    customMinimumOrderQuantity: '',
    customMaterialType: '',
    customComposition: '',
    customStrength: '',
    customDurability: '',
    customThermalProperties: '',
    customChemicalResistance: '',
    customNotes: ''
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  // Pre-populate form data when material changes
  useEffect(() => {
    if (material) {
      setFormData({
        customDensity: material.density?.toString() || '',
        customCarbonFootprint: material.carbon_footprint?.toString() || '',
        customCostPerUnit: material.cost_per_unit?.toString() || '',
        customDescription: material.description || '',
        customUnit: material.unit,
        customCurrency: material.currency || 'USD',
        customMetadata: '',
        isCustom: false,
        reportingPeriod: new Date().toISOString().split('T')[0],
        dataSource: 'Library Import',
        scope: material.scope || '',
        validationStatus: 'pending',
        // Pre-populate new fields
        customEnvironmentalImpactScore: material.environmental_impact_score?.toString() || '',
        customRecyclability: material.recyclability || '',
        customBiodegradability: material.biodegradability || '',
        customAvailability: material.availability || '',
        customLeadTime: material.lead_time || '',
        customMinimumOrderQuantity: material.minimum_order_quantity || '',
        customMaterialType: material.material_type || '',
        customComposition: material.composition || '',
        customStrength: material.strength || '',
        customDurability: material.durability || '',
        customThermalProperties: material.thermal_properties || '',
        customChemicalResistance: material.chemical_resistance || '',
        customNotes: material.notes || ''
      });
    }
  }, [material]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!material || !user) return;

    setLoading(true);
    try {
      // Parse custom metadata
      let customMetadata = {};
      if (formData.customMetadata) {
        try {
          customMetadata = JSON.parse(formData.customMetadata);
        } catch (error) {
          customMetadata = { notes: formData.customMetadata };
        }
      }

      // Create user material in Supabase
      const { data: createdMaterial, error } = await supabase
        .from('user_materials_data')
        .insert({
          organization_id: user.id,
          material_id: material.id,
          name: material.name,
          category: material.category,
          subcategory: material.subcategory,
          description: material.description || '',
          unit: formData.customUnit || material.unit,
          currency: formData.customCurrency,
          custom_density: formData.customDensity ? parseFloat(formData.customDensity) : null,
          custom_carbon_footprint: formData.customCarbonFootprint ? parseFloat(formData.customCarbonFootprint) : null,
          custom_cost_per_unit: formData.customCostPerUnit ? parseFloat(formData.customCostPerUnit) : null,
          custom_metadata: {
            ...customMetadata,
            custom_description: formData.customDescription,
            custom_unit: formData.customUnit,
            custom_currency: formData.customCurrency,
            is_custom: formData.isCustom,
            reporting_period: formData.reportingPeriod,
            data_source: formData.dataSource,
            scope: formData.scope,
            validation_status: formData.validationStatus,
            // Add new custom fields
            custom_environmental_impact_score: formData.customEnvironmentalImpactScore ? parseFloat(formData.customEnvironmentalImpactScore) : undefined,
            custom_recyclability: formData.customRecyclability,
            custom_biodegradability: formData.customBiodegradability,
            custom_availability: formData.customAvailability,
            custom_lead_time: formData.customLeadTime,
            custom_minimum_order_quantity: formData.customMinimumOrderQuantity,
            custom_material_type: formData.customMaterialType,
            custom_composition: formData.customComposition,
            custom_strength: formData.customStrength,
            custom_durability: formData.customDurability,
            custom_thermal_properties: formData.customThermalProperties,
            custom_chemical_resistance: formData.customChemicalResistance,
            custom_notes: formData.customNotes
          },
          is_custom: false
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Emit event to notify other components
      if (createdMaterial) {
        dataSyncEvents.emitMaterialChange('created', createdMaterial.id, user.id);
      }

      toast({
        title: "Material Added Successfully",
        description: `${material.name} has been added to your organization data.`
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error adding material:', error);
      toast({
        title: "Error",
        description: "Failed to add material. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (!material) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Add Material to Organization: {material.name}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Original Material Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Copy className="h-5 w-5" />
                Original Material Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Name</Label>
                  <div className="mt-1 p-2 bg-gray-50 rounded border font-mono text-sm">
                    {material.name}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Category</Label>
                  <div className="mt-1 p-2 bg-gray-50 rounded border">
                    <Badge variant="outline">{material.category}</Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Subcategory</Label>
                  <div className="mt-1 p-2 bg-gray-50 rounded border">
                    {material.subcategory || 'Not specified'}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Unit</Label>
                  <div className="mt-1 p-2 bg-gray-50 rounded border">
                    {material.unit}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Density</Label>
                  <div className="mt-1 p-2 bg-gray-50 rounded border">
                    {material.density ? `${material.density} kg/m³` : 'Not specified'}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Carbon Footprint</Label>
                  <div className="mt-1 p-2 bg-gray-50 rounded border">
                    {material.carbon_footprint ? `${material.carbon_footprint} kg CO2e/kg` : 'Not specified'}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Cost per Unit</Label>
                  <div className="mt-1 p-2 bg-gray-50 rounded border">
                    {material.cost_per_unit ? `${material.cost_per_unit} ${material.currency || 'USD'}` : 'Not specified'}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Sustainability Rating</Label>
                  <div className="mt-1 p-2 bg-gray-50 rounded border">
                    {material.sustainability_rating ? (
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-4 w-4 ${
                              i < material.sustainability_rating! 
                                ? 'text-yellow-400 fill-current' 
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                        <span className="ml-1 text-sm">({material.sustainability_rating}/5)</span>
                      </div>
                    ) : (
                      'Not rated'
                    )}
                  </div>
                </div>
              </div>
              
              {material.description && (
                <div>
                  <Label className="text-sm font-medium">Description</Label>
                  <div className="mt-1 p-2 bg-gray-50 rounded border text-sm">
                    {material.description}
                  </div>
                </div>
              )}

              {material.scope && (
                <div>
                  <Label className="text-sm font-medium">Scope</Label>
                  <div className="mt-1 p-2 bg-gray-50 rounded border">
                    <Badge variant="outline">{material.scope}</Badge>
                  </div>
                </div>
              )}

              {material.certifications && material.certifications.length > 0 && (
                <div>
                  <Label className="text-sm font-medium">Certifications</Label>
                  <div className="mt-1 p-2 bg-gray-50 rounded border">
                    <div className="flex flex-wrap gap-1">
                      {material.certifications && material.certifications.map((cert, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {cert}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Enhanced Material Properties */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Leaf className="h-5 w-5" />
                Environmental & Sustainability Properties
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="customEnvironmentalImpactScore">Environmental Impact Score (0-100)</Label>
                  <Input
                    id="customEnvironmentalImpactScore"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    placeholder="Enter environmental impact score"
                    value={formData.customEnvironmentalImpactScore}
                    onChange={(e) => setFormData(prev => ({ ...prev, customEnvironmentalImpactScore: e.target.value }))}
                  />
                </div>

                <div>
                  <Label htmlFor="customRecyclability">Recyclability</Label>
                  <Select value={formData.customRecyclability} onValueChange={(value) => setFormData(prev => ({ ...prev, customRecyclability: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select recyclability level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="Low">Low</SelectItem>
                      <SelectItem value="Not Recyclable">Not Recyclable</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="customBiodegradability">Biodegradability</Label>
                  <Select value={formData.customBiodegradability} onValueChange={(value) => setFormData(prev => ({ ...prev, customBiodegradability: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select biodegradability" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Biodegradable">Biodegradable</SelectItem>
                      <SelectItem value="Partially Biodegradable">Partially Biodegradable</SelectItem>
                      <SelectItem value="Non-Biodegradable">Non-Biodegradable</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="customAvailability">Availability</Label>
                  <Select value={formData.customAvailability} onValueChange={(value) => setFormData(prev => ({ ...prev, customAvailability: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select availability" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Readily Available">Readily Available</SelectItem>
                      <SelectItem value="Limited Availability">Limited Availability</SelectItem>
                      <SelectItem value="Special Order">Special Order</SelectItem>
                      <SelectItem value="Discontinued">Discontinued</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Procurement Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Clock className="h-5 w-5" />
                Procurement Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="customLeadTime">Lead Time</Label>
                  <Input
                    id="customLeadTime"
                    placeholder="e.g., 2-4 weeks, 1 month"
                    value={formData.customLeadTime}
                    onChange={(e) => setFormData(prev => ({ ...prev, customLeadTime: e.target.value }))}
                  />
                </div>

                <div>
                  <Label htmlFor="customMinimumOrderQuantity">Minimum Order Quantity</Label>
                  <Input
                    id="customMinimumOrderQuantity"
                    placeholder="e.g., 1000 kg, 500 units"
                    value={formData.customMinimumOrderQuantity}
                    onChange={(e) => setFormData(prev => ({ ...prev, customMinimumOrderQuantity: e.target.value }))}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Technical Specifications */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <TestTube className="h-5 w-5" />
                Technical Specifications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="customMaterialType">Material Type</Label>
                  <Input
                    id="customMaterialType"
                    placeholder="e.g., Thermoplastic, Ferrous Metal, Ceramic"
                    value={formData.customMaterialType}
                    onChange={(e) => setFormData(prev => ({ ...prev, customMaterialType: e.target.value }))}
                  />
                </div>

                <div>
                  <Label htmlFor="customComposition">Composition</Label>
                  <Input
                    id="customComposition"
                    placeholder="e.g., Polyethylene terephthalate polymer"
                    value={formData.customComposition}
                    onChange={(e) => setFormData(prev => ({ ...prev, customComposition: e.target.value }))}
                  />
                </div>

                <div>
                  <Label htmlFor="customStrength">Strength Characteristics</Label>
                  <Input
                    id="customStrength"
                    placeholder="e.g., High tensile strength, Good compressive strength"
                    value={formData.customStrength}
                    onChange={(e) => setFormData(prev => ({ ...prev, customStrength: e.target.value }))}
                  />
                </div>

                <div>
                  <Label htmlFor="customDurability">Durability</Label>
                  <Input
                    id="customDurability"
                    placeholder="e.g., Excellent corrosion resistance, 20+ year lifespan"
                    value={formData.customDurability}
                    onChange={(e) => setFormData(prev => ({ ...prev, customDurability: e.target.value }))}
                  />
                </div>

                <div>
                  <Label htmlFor="customThermalProperties">Thermal Properties</Label>
                  <Input
                    id="customThermalProperties"
                    placeholder="e.g., High thermal conductivity, Melting point 250°C"
                    value={formData.customThermalProperties}
                    onChange={(e) => setFormData(prev => ({ ...prev, customThermalProperties: e.target.value }))}
                  />
                </div>

                <div>
                  <Label htmlFor="customChemicalResistance">Chemical Resistance</Label>
                  <Input
                    id="customChemicalResistance"
                    placeholder="e.g., Resistant to most solvents, Susceptible to acids"
                    value={formData.customChemicalResistance}
                    onChange={(e) => setFormData(prev => ({ ...prev, customChemicalResistance: e.target.value }))}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Customization Options */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Settings className="h-5 w-5" />
                Customize for Your Organization
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="isCustom"
                  checked={formData.isCustom}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isCustom: checked }))}
                />
                <Label htmlFor="isCustom">Mark as custom material</Label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="customDensity">Custom Density (kg/m³)</Label>
                  <Input
                    id="customDensity"
                    type="number"
                    step="0.0001"
                    placeholder="Enter custom density"
                    value={formData.customDensity}
                    onChange={(e) => setFormData(prev => ({ ...prev, customDensity: e.target.value }))}
                  />
                </div>

                <div>
                  <Label htmlFor="customCarbonFootprint">Custom Carbon Footprint (kg CO2e/kg)</Label>
                  <Input
                    id="customCarbonFootprint"
                    type="number"
                    step="0.0001"
                    placeholder="Enter custom carbon footprint"
                    value={formData.customCarbonFootprint}
                    onChange={(e) => setFormData(prev => ({ ...prev, customCarbonFootprint: e.target.value }))}
                  />
                </div>

                <div>
                  <Label htmlFor="customCostPerUnit">Custom Cost per Unit</Label>
                  <Input
                    id="customCostPerUnit"
                    type="number"
                    step="0.01"
                    placeholder="Enter custom cost"
                    value={formData.customCostPerUnit}
                    onChange={(e) => setFormData(prev => ({ ...prev, customCostPerUnit: e.target.value }))}
                  />
                </div>

                <div>
                  <Label htmlFor="customCurrency">Currency</Label>
                  <CurrencySelect
                    value={formData.customCurrency}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, customCurrency: value }))}
                  />
                </div>

                <div>
                  <Label htmlFor="customUnit">Custom Unit</Label>
                  <Input
                    id="customUnit"
                    placeholder="Custom unit (optional)"
                    value={formData.customUnit}
                    onChange={(e) => setFormData(prev => ({ ...prev, customUnit: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="customDescription">Custom Description</Label>
                <Textarea
                  id="customDescription"
                  placeholder="Custom description for your organization (optional)"
                  value={formData.customDescription}
                  onChange={(e) => setFormData(prev => ({ ...prev, customDescription: e.target.value }))}
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="customNotes">Additional Notes</Label>
                <Textarea
                  id="customNotes"
                  placeholder="Enter additional notes, observations, or specific details for your organization"
                  value={formData.customNotes}
                  onChange={(e) => setFormData(prev => ({ ...prev, customNotes: e.target.value }))}
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="customMetadata">Additional Information</Label>
                <Textarea
                  id="customMetadata"
                  placeholder="Enter additional notes, supplier information, or other relevant details for your organization"
                  value={formData.customMetadata}
                  onChange={(e) => setFormData(prev => ({ ...prev, customMetadata: e.target.value }))}
                  rows={3}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Optional additional information specific to your organization's use of this material
                </p>
              </div>

              {/* Additional Required Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="reportingPeriod">Reporting Period</Label>
                  <Input
                    id="reportingPeriod"
                    type="date"
                    value={formData.reportingPeriod}
                    onChange={(e) => setFormData(prev => ({ ...prev, reportingPeriod: e.target.value }))}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="dataSource">Data Source</Label>
                  <Input
                    id="dataSource"
                    placeholder="e.g., Supplier Data, Internal Systems, etc."
                    value={formData.dataSource}
                    onChange={(e) => setFormData(prev => ({ ...prev, dataSource: e.target.value }))}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="scope">Scope</Label>
                  <Select value={formData.scope} onValueChange={(value) => setFormData(prev => ({ ...prev, scope: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select scope" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Scope 1">Scope 1</SelectItem>
                      <SelectItem value="Scope 2">Scope 2</SelectItem>
                      <SelectItem value="Scope 3">Scope 3</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="validationStatus">Validation Status</Label>
                  <Select value={formData.validationStatus} onValueChange={(value) => setFormData(prev => ({ ...prev, validationStatus: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="validated">Validated</SelectItem>
                      <SelectItem value="review_required">Review Required</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Material Properties Information */}
          {(material.emission_factor !== null && material.emission_factor !== undefined) || 
           (material.calorific_value !== null && material.calorific_value !== undefined) ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Zap className="h-5 w-5" />
                  Material Properties
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {material.emission_factor !== null && material.emission_factor !== undefined && (
                  <div>
                    <Label className="text-sm font-medium">Emission Factor</Label>
                    <div className="mt-1 p-2 bg-gray-50 rounded border text-sm">
                      {material.emission_factor} kg CO2e/kg
                    </div>
                  </div>
                )}

                {material.calorific_value !== null && material.calorific_value !== undefined && (
                  <div>
                    <Label className="text-sm font-medium">Calorific Value</Label>
                    <div className="mt-1 p-2 bg-gray-50 rounded border text-sm">
                      {material.calorific_value} MJ/kg
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : null}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Adding...' : 'Add to Organization'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};





// import React, { useState, useEffect } from 'react';
// import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
// import { Button } from '@/components/ui/button';
// import { Input } from '@/components/ui/input';
// import { Label } from '@/components/ui/label';
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
// import { Textarea } from '@/components/ui/textarea';
// import { Badge } from '@/components/ui/badge';
// import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
// import { Switch } from '@/components/ui/switch';
// import { useToast } from '@/hooks/use-toast';
// import { UnifiedMaterial } from '@/types/unifiedLibrary';
// import { enhancedOrganizationalDataService } from '@/services/enhancedOrganizationalDataService';
// import { indexedDBService } from '@/services/indexedDBService';
// import { dataSyncEvents } from '@/utils/dataSyncEvents';
// import { useAuth } from '@/components/Auth/AuthProvider';
// import { CurrencySelect } from '@/components/CurrencySelect';
// import { Package, Settings, Database, Copy, Star, Leaf, Zap, Recycle, AlertTriangle, Clock, Box, TestTube, Shield, Thermometer, Droplets } from 'lucide-react';

// interface EnhancedMaterialModalProps {
//   material: UnifiedMaterial | null;
//   isOpen: boolean;
//   onClose: () => void;
//   onSuccess: () => void;
// }

// export const EnhancedMaterialModal = ({ material, isOpen, onClose, onSuccess }: EnhancedMaterialModalProps) => {
//   const [formData, setFormData] = useState({
//     customDensity: '',
//     customCarbonFootprint: '',
//     customCostPerUnit: '',
//     customDescription: '',
//     customUnit: '',
//     customCurrency: 'USD',
//     customMetadata: '',
//     isCustom: false,
//     reportingPeriod: new Date().toISOString().split('T')[0],
//     dataSource: 'Library Import',
//     scope: '',
//     validationStatus: 'pending',
//     // New fields for enhanced material properties
//     customEnvironmentalImpactScore: '',
//     customRecyclability: '',
//     customBiodegradability: '',
//     customAvailability: '',
//     customLeadTime: '',
//     customMinimumOrderQuantity: '',
//     customMaterialType: '',
//     customComposition: '',
//     customStrength: '',
//     customDurability: '',
//     customThermalProperties: '',
//     customChemicalResistance: '',
//     customNotes: ''
//   });
//   const [loading, setLoading] = useState(false);
//   const { toast } = useToast();
//   const { user } = useAuth();

//   // Pre-populate form data when material changes
//   useEffect(() => {
//     if (material) {
//       setFormData({
//         customDensity: material.density?.toString() || '',
//         customCarbonFootprint: material.carbon_footprint?.toString() || '',
//         customCostPerUnit: material.cost_per_unit?.toString() || '',
//         customDescription: material.description || '',
//         customUnit: material.unit,
//         customCurrency: material.currency || 'USD',
//         customMetadata: '',
//         isCustom: false,
//         reportingPeriod: new Date().toISOString().split('T')[0],
//         dataSource: 'Library Import',
//         scope: material.scope || '',
//         validationStatus: 'pending',
//         // Pre-populate new fields
//         customEnvironmentalImpactScore: material.environmental_impact_score?.toString() || '',
//         customRecyclability: material.recyclability || '',
//         customBiodegradability: material.biodegradability || '',
//         customAvailability: material.availability || '',
//         customLeadTime: material.lead_time || '',
//         customMinimumOrderQuantity: material.minimum_order_quantity || '',
//         customMaterialType: material.material_type || '',
//         customComposition: material.composition || '',
//         customStrength: material.strength || '',
//         customDurability: material.durability || '',
//         customThermalProperties: material.thermal_properties || '',
//         customChemicalResistance: material.chemical_resistance || '',
//         customNotes: material.notes || ''
//       });
//     }
//   }, [material]);

//   const handleSubmit = async (e: React.FormEvent) => {
//     e.preventDefault();
//     if (!material || !user) return;

//     setLoading(true);
//     try {
//       // Initialize IndexedDB service
//       await indexedDBService.initialize();
//       await enhancedOrganizationalDataService.initialize();

//       // Parse custom metadata
//       let customMetadata = {};
//       if (formData.customMetadata) {
//         try {
//           customMetadata = JSON.parse(formData.customMetadata);
//         } catch (error) {
//           customMetadata = { notes: formData.customMetadata };
//         }
//       }

//       // Create user material in IndexedDB
//       const result = await enhancedOrganizationalDataService.createUserMaterial(user.id, {
//         organization_id: user.id,
//         material_id: material.id,
//         name: material.name,
//         category: material.category,
//         subcategory: material.subcategory,
//         description: material.description || '',
//         unit: formData.customUnit || material.unit,
//         currency: formData.customCurrency,
//         custom_density: formData.customDensity ? parseFloat(formData.customDensity) : undefined,
//         custom_carbon_footprint: formData.customCarbonFootprint ? parseFloat(formData.customCarbonFootprint) : undefined,
//         custom_cost_per_unit: formData.customCostPerUnit ? parseFloat(formData.customCostPerUnit) : undefined,
//         custom_metadata: {
//           ...customMetadata,
//           custom_description: formData.customDescription,
//           custom_unit: formData.customUnit,
//           custom_currency: formData.customCurrency,
//           is_custom: formData.isCustom,
//           reporting_period: formData.reportingPeriod,
//           data_source: formData.dataSource,
//           scope: formData.scope,
//           validation_status: formData.validationStatus,
//           // Add new custom fields
//           custom_environmental_impact_score: formData.customEnvironmentalImpactScore ? parseFloat(formData.customEnvironmentalImpactScore) : undefined,
//           custom_recyclability: formData.customRecyclability,
//           custom_biodegradability: formData.customBiodegradability,
//           custom_availability: formData.customAvailability,
//           custom_lead_time: formData.customLeadTime,
//           custom_minimum_order_quantity: formData.customMinimumOrderQuantity,
//           custom_material_type: formData.customMaterialType,
//           custom_composition: formData.customComposition,
//           custom_strength: formData.customStrength,
//           custom_durability: formData.customDurability,
//           custom_thermal_properties: formData.customThermalProperties,
//           custom_chemical_resistance: formData.customChemicalResistance,
//           custom_notes: formData.customNotes
//         },
//         is_custom: false
//       });

//       if (!result.success) {
//         throw new Error(result.error || 'Failed to create user material');
//       }

//       // Emit event to notify other components
//       if (result.material) {
//         dataSyncEvents.emitMaterialChange('created', result.material.id, user.id);
//       }

//       toast({
//         title: "Material Added Successfully",
//         description: `${material.name} has been added to your organization data in IndexedDB.`
//       });

//       onSuccess();
//       onClose();
//     } catch (error) {
//       console.error('Error adding material:', error);
//       toast({
//         title: "Error",
//         description: "Failed to add material to IndexedDB. Please try again.",
//         variant: "destructive"
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   if (!material) return null;

//   return (
//     <Dialog open={isOpen} onOpenChange={onClose}>
//       <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
//         <DialogHeader>
//           <DialogTitle className="flex items-center gap-2">
//             <Database className="h-5 w-5" />
//             Add Material to Organization: {material.name}
//           </DialogTitle>
//         </DialogHeader>

//         <form onSubmit={handleSubmit} className="space-y-6">
//           {/* Original Material Information */}
//           <Card>
//             <CardHeader>
//               <CardTitle className="flex items-center gap-2 text-lg">
//                 <Copy className="h-5 w-5" />
//                 Original Material Information
//               </CardTitle>
//             </CardHeader>
//             <CardContent className="space-y-4">
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                 <div>
//                   <Label className="text-sm font-medium">Name</Label>
//                   <div className="mt-1 p-2 bg-gray-50 rounded border font-mono text-sm">
//                     {material.name}
//                   </div>
//                 </div>
//                 <div>
//                   <Label className="text-sm font-medium">Category</Label>
//                   <div className="mt-1 p-2 bg-gray-50 rounded border">
//                     <Badge variant="outline">{material.category}</Badge>
//                   </div>
//                 </div>
//                 <div>
//                   <Label className="text-sm font-medium">Subcategory</Label>
//                   <div className="mt-1 p-2 bg-gray-50 rounded border">
//                     {material.subcategory || 'Not specified'}
//                   </div>
//                 </div>
//                 <div>
//                   <Label className="text-sm font-medium">Unit</Label>
//                   <div className="mt-1 p-2 bg-gray-50 rounded border">
//                     {material.unit}
//                   </div>
//                 </div>
//                 <div>
//                   <Label className="text-sm font-medium">Density</Label>
//                   <div className="mt-1 p-2 bg-gray-50 rounded border">
//                     {material.density ? `${material.density} kg/m³` : 'Not specified'}
//                   </div>
//                 </div>
//                 <div>
//                   <Label className="text-sm font-medium">Carbon Footprint</Label>
//                   <div className="mt-1 p-2 bg-gray-50 rounded border">
//                     {material.carbon_footprint ? `${material.carbon_footprint} kg CO2e/kg` : 'Not specified'}
//                   </div>
//                 </div>
//                 <div>
//                   <Label className="text-sm font-medium">Cost per Unit</Label>
//                   <div className="mt-1 p-2 bg-gray-50 rounded border">
//                     {material.cost_per_unit ? `${material.cost_per_unit} ${material.currency || 'USD'}` : 'Not specified'}
//                   </div>
//                 </div>
//                 <div>
//                   <Label className="text-sm font-medium">Sustainability Rating</Label>
//                   <div className="mt-1 p-2 bg-gray-50 rounded border">
//                     {material.sustainability_rating ? (
//                       <div className="flex items-center gap-1">
//                         {[...Array(5)].map((_, i) => (
//                           <Star
//                             key={i}
//                             className={`h-4 w-4 ${
//                               i < material.sustainability_rating! 
//                                 ? 'text-yellow-400 fill-current' 
//                                 : 'text-gray-300'
//                             }`}
//                           />
//                         ))}
//                         <span className="ml-1 text-sm">({material.sustainability_rating}/5)</span>
//                       </div>
//                     ) : (
//                       'Not rated'
//                     )}
//                   </div>
//                 </div>
//               </div>
              
//               {material.description && (
//                 <div>
//                   <Label className="text-sm font-medium">Description</Label>
//                   <div className="mt-1 p-2 bg-gray-50 rounded border text-sm">
//                     {material.description}
//                   </div>
//                 </div>
//               )}

//               {material.scope && (
//                 <div>
//                   <Label className="text-sm font-medium">Scope</Label>
//                   <div className="mt-1 p-2 bg-gray-50 rounded border">
//                     <Badge variant="outline">{material.scope}</Badge>
//                   </div>
//                 </div>
//               )}

//               {material.certifications && material.certifications.length > 0 && (
//                 <div>
//                   <Label className="text-sm font-medium">Certifications</Label>
//                   <div className="mt-1 p-2 bg-gray-50 rounded border">
//                     <div className="flex flex-wrap gap-1">
//                       {material.certifications && material.certifications.map((cert, index) => (
//                         <Badge key={index} variant="secondary" className="text-xs">
//                           {cert}
//                         </Badge>
//                       ))}
//                     </div>
//                   </div>
//                 </div>
//               )}
//             </CardContent>
//           </Card>

//           {/* Enhanced Material Properties */}
//           <Card>
//             <CardHeader>
//               <CardTitle className="flex items-center gap-2 text-lg">
//                 <Leaf className="h-5 w-5" />
//                 Environmental & Sustainability Properties
//               </CardTitle>
//             </CardHeader>
//             <CardContent className="space-y-4">
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                 <div>
//                   <Label htmlFor="customEnvironmentalImpactScore">Environmental Impact Score (0-100)</Label>
//                   <Input
//                     id="customEnvironmentalImpactScore"
//                     type="number"
//                     min="0"
//                     max="100"
//                     step="0.1"
//                     placeholder="Enter environmental impact score"
//                     value={formData.customEnvironmentalImpactScore}
//                     onChange={(e) => setFormData(prev => ({ ...prev, customEnvironmentalImpactScore: e.target.value }))}
//                   />
//                 </div>

//                 <div>
//                   <Label htmlFor="customRecyclability">Recyclability</Label>
//                   <Select value={formData.customRecyclability} onValueChange={(value) => setFormData(prev => ({ ...prev, customRecyclability: value }))}>
//                     <SelectTrigger>
//                       <SelectValue placeholder="Select recyclability level" />
//                     </SelectTrigger>
//                     <SelectContent>
//                       <SelectItem value="High">High</SelectItem>
//                       <SelectItem value="Medium">Medium</SelectItem>
//                       <SelectItem value="Low">Low</SelectItem>
//                       <SelectItem value="Not Recyclable">Not Recyclable</SelectItem>
//                     </SelectContent>
//                   </Select>
//                 </div>

//                 <div>
//                   <Label htmlFor="customBiodegradability">Biodegradability</Label>
//                   <Select value={formData.customBiodegradability} onValueChange={(value) => setFormData(prev => ({ ...prev, customBiodegradability: value }))}>
//                     <SelectTrigger>
//                       <SelectValue placeholder="Select biodegradability" />
//                     </SelectTrigger>
//                     <SelectContent>
//                       <SelectItem value="Biodegradable">Biodegradable</SelectItem>
//                       <SelectItem value="Partially Biodegradable">Partially Biodegradable</SelectItem>
//                       <SelectItem value="Non-Biodegradable">Non-Biodegradable</SelectItem>
//                     </SelectContent>
//                   </Select>
//                 </div>

//                 <div>
//                   <Label htmlFor="customAvailability">Availability</Label>
//                   <Select value={formData.customAvailability} onValueChange={(value) => setFormData(prev => ({ ...prev, customAvailability: value }))}>
//                     <SelectTrigger>
//                       <SelectValue placeholder="Select availability" />
//                     </SelectTrigger>
//                     <SelectContent>
//                       <SelectItem value="Readily Available">Readily Available</SelectItem>
//                       <SelectItem value="Limited Availability">Limited Availability</SelectItem>
//                       <SelectItem value="Special Order">Special Order</SelectItem>
//                       <SelectItem value="Discontinued">Discontinued</SelectItem>
//                     </SelectContent>
//                   </Select>
//                 </div>
//               </div>
//             </CardContent>
//           </Card>

//           {/* Procurement Information */}
//           <Card>
//             <CardHeader>
//               <CardTitle className="flex items-center gap-2 text-lg">
//                 <Clock className="h-5 w-5" />
//                 Procurement Information
//               </CardTitle>
//             </CardHeader>
//             <CardContent className="space-y-4">
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                 <div>
//                   <Label htmlFor="customLeadTime">Lead Time</Label>
//                   <Input
//                     id="customLeadTime"
//                     placeholder="e.g., 2-4 weeks, 1 month"
//                     value={formData.customLeadTime}
//                     onChange={(e) => setFormData(prev => ({ ...prev, customLeadTime: e.target.value }))}
//                   />
//                 </div>

//                 <div>
//                   <Label htmlFor="customMinimumOrderQuantity">Minimum Order Quantity</Label>
//                   <Input
//                     id="customMinimumOrderQuantity"
//                     placeholder="e.g., 1000 kg, 500 units"
//                     value={formData.customMinimumOrderQuantity}
//                     onChange={(e) => setFormData(prev => ({ ...prev, customMinimumOrderQuantity: e.target.value }))}
//                   />
//                 </div>
//               </div>
//             </CardContent>
//           </Card>

//           {/* Technical Specifications */}
//           <Card>
//             <CardHeader>
//               <CardTitle className="flex items-center gap-2 text-lg">
//                 <TestTube className="h-5 w-5" />
//                 Technical Specifications
//               </CardTitle>
//             </CardHeader>
//             <CardContent className="space-y-4">
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                 <div>
//                   <Label htmlFor="customMaterialType">Material Type</Label>
//                   <Input
//                     id="customMaterialType"
//                     placeholder="e.g., Thermoplastic, Ferrous Metal, Ceramic"
//                     value={formData.customMaterialType}
//                     onChange={(e) => setFormData(prev => ({ ...prev, customMaterialType: e.target.value }))}
//                   />
//                 </div>

//                 <div>
//                   <Label htmlFor="customComposition">Composition</Label>
//                   <Input
//                     id="customComposition"
//                     placeholder="e.g., Polyethylene terephthalate polymer"
//                     value={formData.customComposition}
//                     onChange={(e) => setFormData(prev => ({ ...prev, customComposition: e.target.value }))}
//                   />
//                 </div>

//                 <div>
//                   <Label htmlFor="customStrength">Strength Characteristics</Label>
//                   <Input
//                     id="customStrength"
//                     placeholder="e.g., High tensile strength, Good compressive strength"
//                     value={formData.customStrength}
//                     onChange={(e) => setFormData(prev => ({ ...prev, customStrength: e.target.value }))}
//                   />
//                 </div>

//                 <div>
//                   <Label htmlFor="customDurability">Durability</Label>
//                   <Input
//                     id="customDurability"
//                     placeholder="e.g., Excellent corrosion resistance, 20+ year lifespan"
//                     value={formData.customDurability}
//                     onChange={(e) => setFormData(prev => ({ ...prev, customDurability: e.target.value }))}
//                   />
//                 </div>

//                 <div>
//                   <Label htmlFor="customThermalProperties">Thermal Properties</Label>
//                   <Input
//                     id="customThermalProperties"
//                     placeholder="e.g., High thermal conductivity, Melting point 250°C"
//                     value={formData.customThermalProperties}
//                     onChange={(e) => setFormData(prev => ({ ...prev, customThermalProperties: e.target.value }))}
//                   />
//                 </div>

//                 <div>
//                   <Label htmlFor="customChemicalResistance">Chemical Resistance</Label>
//                   <Input
//                     id="customChemicalResistance"
//                     placeholder="e.g., Resistant to most solvents, Susceptible to acids"
//                     value={formData.customChemicalResistance}
//                     onChange={(e) => setFormData(prev => ({ ...prev, customChemicalResistance: e.target.value }))}
//                   />
//                 </div>
//               </div>
//             </CardContent>
//           </Card>

//           {/* Customization Options */}
//           <Card>
//             <CardHeader>
//               <CardTitle className="flex items-center gap-2 text-lg">
//                 <Settings className="h-5 w-5" />
//                 Customize for Your Organization
//               </CardTitle>
//             </CardHeader>
//             <CardContent className="space-y-4">
//               <div className="flex items-center space-x-2">
//                 <Switch
//                   id="isCustom"
//                   checked={formData.isCustom}
//                   onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isCustom: checked }))}
//                 />
//                 <Label htmlFor="isCustom">Mark as custom material</Label>
//               </div>

//               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                 <div>
//                   <Label htmlFor="customDensity">Custom Density (kg/m³)</Label>
//                   <Input
//                     id="customDensity"
//                     type="number"
//                     step="0.0001"
//                     placeholder="Enter custom density"
//                     value={formData.customDensity}
//                     onChange={(e) => setFormData(prev => ({ ...prev, customDensity: e.target.value }))}
//                   />
//                 </div>

//                 <div>
//                   <Label htmlFor="customCarbonFootprint">Custom Carbon Footprint (kg CO2e/kg)</Label>
//                   <Input
//                     id="customCarbonFootprint"
//                     type="number"
//                     step="0.0001"
//                     placeholder="Enter custom carbon footprint"
//                     value={formData.customCarbonFootprint}
//                     onChange={(e) => setFormData(prev => ({ ...prev, customCarbonFootprint: e.target.value }))}
//                   />
//                 </div>

//                 <div>
//                   <Label htmlFor="customCostPerUnit">Custom Cost per Unit</Label>
//                   <Input
//                     id="customCostPerUnit"
//                     type="number"
//                     step="0.01"
//                     placeholder="Enter custom cost"
//                     value={formData.customCostPerUnit}
//                     onChange={(e) => setFormData(prev => ({ ...prev, customCostPerUnit: e.target.value }))}
//                   />
//                 </div>

//                 <div>
//                   <Label htmlFor="customCurrency">Currency</Label>
//                   <CurrencySelect
//                     value={formData.customCurrency}
//                     onValueChange={(value) => setFormData(prev => ({ ...prev, customCurrency: value }))}
//                   />
//                 </div>

//                 <div>
//                   <Label htmlFor="customUnit">Custom Unit</Label>
//                   <Input
//                     id="customUnit"
//                     placeholder="Custom unit (optional)"
//                     value={formData.customUnit}
//                     onChange={(e) => setFormData(prev => ({ ...prev, customUnit: e.target.value }))}
//                   />
//                 </div>
//               </div>

//               <div>
//                 <Label htmlFor="customDescription">Custom Description</Label>
//                 <Textarea
//                   id="customDescription"
//                   placeholder="Custom description for your organization (optional)"
//                   value={formData.customDescription}
//                   onChange={(e) => setFormData(prev => ({ ...prev, customDescription: e.target.value }))}
//                   rows={3}
//                 />
//               </div>

//               <div>
//                 <Label htmlFor="customNotes">Additional Notes</Label>
//                 <Textarea
//                   id="customNotes"
//                   placeholder="Enter additional notes, observations, or specific details for your organization"
//                   value={formData.customNotes}
//                   onChange={(e) => setFormData(prev => ({ ...prev, customNotes: e.target.value }))}
//                   rows={3}
//                 />
//               </div>

//               <div>
//                 <Label htmlFor="customMetadata">Additional Information</Label>
//                 <Textarea
//                   id="customMetadata"
//                   placeholder="Enter additional notes, supplier information, or other relevant details for your organization"
//                   value={formData.customMetadata}
//                   onChange={(e) => setFormData(prev => ({ ...prev, customMetadata: e.target.value }))}
//                   rows={3}
//                 />
//                 <p className="text-xs text-gray-500 mt-1">
//                   Optional additional information specific to your organization's use of this material
//                 </p>
//               </div>

//               {/* Additional Required Fields */}
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                 <div>
//                   <Label htmlFor="reportingPeriod">Reporting Period</Label>
//                   <Input
//                     id="reportingPeriod"
//                     type="date"
//                     value={formData.reportingPeriod}
//                     onChange={(e) => setFormData(prev => ({ ...prev, reportingPeriod: e.target.value }))}
//                     required
//                   />
//                 </div>

//                 <div>
//                   <Label htmlFor="dataSource">Data Source</Label>
//                   <Input
//                     id="dataSource"
//                     placeholder="e.g., Supplier Data, Internal Systems, etc."
//                     value={formData.dataSource}
//                     onChange={(e) => setFormData(prev => ({ ...prev, dataSource: e.target.value }))}
//                     required
//                   />
//                 </div>

//                 <div>
//                   <Label htmlFor="scope">Scope</Label>
//                   <Select value={formData.scope} onValueChange={(value) => setFormData(prev => ({ ...prev, scope: value }))}>
//                     <SelectTrigger>
//                       <SelectValue placeholder="Select scope" />
//                     </SelectTrigger>
//                     <SelectContent>
//                       <SelectItem value="Scope 1">Scope 1</SelectItem>
//                       <SelectItem value="Scope 2">Scope 2</SelectItem>
//                       <SelectItem value="Scope 3">Scope 3</SelectItem>
//                     </SelectContent>
//                   </Select>
//                 </div>

//                 <div>
//                   <Label htmlFor="validationStatus">Validation Status</Label>
//                   <Select value={formData.validationStatus} onValueChange={(value) => setFormData(prev => ({ ...prev, validationStatus: value }))}>
//                     <SelectTrigger>
//                       <SelectValue placeholder="Select status" />
//                     </SelectTrigger>
//                     <SelectContent>
//                       <SelectItem value="pending">Pending</SelectItem>
//                       <SelectItem value="validated">Validated</SelectItem>
//                       <SelectItem value="review_required">Review Required</SelectItem>
//                     </SelectContent>
//                   </Select>
//                 </div>
//               </div>
//             </CardContent>
//           </Card>

//           {/* Material Properties Information */}
//           {(material.emission_factor !== null && material.emission_factor !== undefined) || 
//            (material.calorific_value !== null && material.calorific_value !== undefined) ? (
//             <Card>
//               <CardHeader>
//                 <CardTitle className="flex items-center gap-2 text-lg">
//                   <Zap className="h-5 w-5" />
//                   Material Properties
//                 </CardTitle>
//               </CardHeader>
//               <CardContent className="space-y-4">
//                 {material.emission_factor !== null && material.emission_factor !== undefined && (
//                   <div>
//                     <Label className="text-sm font-medium">Emission Factor</Label>
//                     <div className="mt-1 p-2 bg-gray-50 rounded border text-sm">
//                       {material.emission_factor} kg CO2e/kg
//                     </div>
//                   </div>
//                 )}

//                 {material.calorific_value !== null && material.calorific_value !== undefined && (
//                   <div>
//                     <Label className="text-sm font-medium">Calorific Value</Label>
//                     <div className="mt-1 p-2 bg-gray-50 rounded border text-sm">
//                       {material.calorific_value} MJ/kg
//                     </div>
//                   </div>
//                 )}
//               </CardContent>
//             </Card>
//           ) : null}

//           {/* Action Buttons */}
//           <div className="flex justify-end gap-3">
//             <Button type="button" variant="outline" onClick={onClose}>
//               Cancel
//             </Button>
//             <Button type="submit" disabled={loading}>
//               {loading ? 'Adding...' : 'Add to Organization'}
//             </Button>
//           </div>
//         </form>
//       </DialogContent>
//     </Dialog>
//   );
// }; 